import PersonAddAltIcon from "@mui/icons-material/PersonAddAlt";
import Container from "@mui/material/Container";
import { useState } from "react";
import { useNavigate } from "react-router-dom";
import CreateItemButton from "../../components/CreateItemButton.js";
import DataNotFound from "../../components/DataNotFound.js";
import ErrorDialog from "../../components/dialogs/ErrorDialog.js";
import LoadingBackdrop from "../../components/LoadingBackdrop.js";
import PageList from "../../components/page-list/PageList.js";
import { useUser } from "../../components/UserContext.js";
import { getHosts, postHost } from "../../lib/apiConfig.js";
import { PAGE_LIST_CONTAINER_SX, ROUTES } from "../../lib/constants.js";
import { filterVisibleHosts } from "../../lib/filterVisibleHosts.js";
import { useFetch } from "../../lib/hooks/useFetch.js";
import CreateHostDialog from "../behaviour/components/dialogs/CreateHostDialog.js";

export default function HostsList({ locales }) {
  const { canEdit, canViewOtherHidden, hostEmployeeId, isAdmin, runId } =
    useUser();
  const navigate = useNavigate();
  const [hosts, setHosts] = useState(null);
  const [serial, setSerial] = useState("");
  const [showCreateHostDialog, setShowCreateHostDialog] = useState(false);
  const [showError, setShowError] = useState(false);
  const [errorMessage, setErrorMessage] = useState(null);

  const { isFetching: isFetchingHosts } = useFetch({
    endpoint: getHosts({ runId }),
    mustFetch: !hosts,
    onSuccess: (response) => {
      const visibleHosts = filterVisibleHosts({
        canViewOtherHidden,
        hostEmployeeId,
        hosts: response,
      });

      setHosts(isAdmin ? response : visibleHosts);
    },
    onFail: (error) => {
      setErrorMessage(error.message);
      setShowError(true);
    },
  });

  const { fetchData: createHost, isFetching: isCreatingHost } = useFetch({
    endpoint: postHost({ runId }),
    onSuccess: (response) => {
      const hostId = response.id;

      navigate(`${ROUTES.BEHAVIOUR.BASE.replace(":runId", runId)}/${hostId}`, {
        replace: true,
        state: { id: hostId },
      });
    },
    onFail: (error) => {
      setErrorMessage(error.message);
      setShowError(true);
    },
  });

  const handleRegisterClick = () => {
    createHost({ serial });
  };

  return isFetchingHosts || isCreatingHost ? (
    <LoadingBackdrop />
  ) : (
    <Container disableGutters sx={PAGE_LIST_CONTAINER_SX}>
      {canEdit && (
        <CreateItemButton
          icon={<PersonAddAltIcon />}
          onClick={() => setShowCreateHostDialog(true)}
          text={locales["host.create"]}
        />
      )}
      {Array.isArray(hosts) ? (
        <PageList
          items={hosts}
          locales={locales}
          pageName="hosts"
          primaryTextProp="serial"
          searchBoxLabel={locales["hosts.search"]}
          secondaryTextProp="character.name"
        />
      ) : (
        <DataNotFound text={locales["hosts.notFound"]} />
      )}
      <CreateHostDialog
        locales={locales}
        onInput={({ target }) => {
          setSerial(target.value?.replaceAll(" ", ""));
        }}
        onClose={() => {
          setShowCreateHostDialog(false);
          setSerial("");
        }}
        onConfirm={handleRegisterClick}
        serial={serial}
        show={showCreateHostDialog}
      />
      <ErrorDialog
        locales={locales}
        message={errorMessage}
        onClose={() => {
          setShowError(false);
          setErrorMessage(null);
        }}
        show={showError}
      />
    </Container>
  );
}
